// ReportTable.tsx
import { useState, useMemo, useRef, useCallback, useEffect, memo } from "react";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { PencilIcon, TrashIcon, ArrowsUpDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { ReportConfig } from "./reportConfigs";
import AddEditGroupModal from "./modals/addEditGroupModal";
// import EditUserModal from "./modals/GenericModal";
// import NewUserModal from "./modals/GenericModal";

type Props = {
  config: ReportConfig;
  data: any[];
  onDataUpdate: (data: any[]) => void;
  onSave: (row: Record<string, any>, isNew: boolean) => Promise<any>;
  onDelete: any;
  onBulkDelete: any;
};
type SelectedRowIds = Record<string, boolean>;
type ModalState = { open: boolean; row: any | null; isNew: boolean };

const columnHelper = createColumnHelper<any>();

// ─── Stable search row — lives completely outside TanStack, never re-mounts ──
const SearchRow = memo(({ cols, sortConfig, onSearch, onSort, allSelected, onSelectAll }: {
  cols: { key: string; label?: string }[];
  sortConfig: { key: string; dir: "asc" | "desc" };
  onSearch: (key: string, val: string) => void;
  onSort: (key: string) => void;
  allSelected: boolean;           // ← add
  onSelectAll: () => void;        // ← add
}) => {
  const debounces = useRef<Record<string, NodeJS.Timeout>>({});
  return (
    <>
      {/* Label row */}
      <tr className="bg-gray-50">
        <th className="border p-2 w-8" >  <input
          type="checkbox"
          checked={allSelected}
          onChange={onSelectAll}
        /></th>
        <th className="border p-2 text-md">Edit</th>
        <th className="border p-2 text-md">Delete</th>
        {cols.map((col) => (
          <th key={col.key} className="border p-2 text-md">
            {col.label ?? col.key}
          </th>
        ))}
      </tr>
      {/* Search + sort row */}
      <tr className="bg-gray-50">
        <th className="border p-1" />
        <th className="border p-1" />
        <th className="border p-1" />
        {cols.map((col) => (
          <th key={col.key} className="border p-1">
            <div className="flex items-center justify-center gap-1">
              <input
                type="text"
                placeholder=""
                onChange={(e) => {
                  const val = e.target.value;
                  clearTimeout(debounces.current[col.key]);
                  debounces.current[col.key] = setTimeout(() => onSearch(col.key, val), 300);
                }}
                className="border border-gray-300 rounded text-xs w-[140px]"
              />
              <button
                type="button"
                onClick={() => onSort(col.key)}
                className="flex-shrink-0"
              >
                <ArrowsUpDownIcon
                  className={`h-3.5 w-3.5 ${sortConfig.key === col.key ? "text-orange-500" : "text-gray-400"}`}
                />
              </button>
            </div>
          </th>
        ))}
      </tr>
    </>
  );
  // Never re-render SearchRow from outside — inputs manage themselves
  // Only re-render when sort icon needs to update (sortConfig change)
}, (prev, next) => prev.sortConfig.key === next.sortConfig.key && prev.sortConfig.dir === next.sortConfig.dir && prev.cols === next.cols &&
  prev.allSelected === next.allSelected);

// ─────────────────────────────────────────────────────────────────────────────

const ReportTable = ({ config, data, onDataUpdate, onSave, onDelete, onBulkDelete }: Props) => {
  const visibleCols = useMemo(() => config.columns.filter((c) => !c.hidden), [config]);

  const [searchInputs, setSearchInputs] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "", dir: "asc" });
  const [selectedRowIds, setSelectedRowIds] = useState<SelectedRowIds>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filteredData, setFilteredData] = useState<any[]>(data);
  const [modal, setModal] = useState<ModalState>({ open: false, row: null, isNew: false });
  const [bulkDeleteMsg, setBulkDeleteMsg] = useState<boolean>(false)

  // Boolean-aware filter
  useEffect(() => {
    const boolKeys = new Set(config.columns.filter((c) => c.boolean).map((c) => c.key));
    const result = data.filter((row) =>
      Object.entries(searchInputs).every(([key, term]) => {
        if (!term) return true;
        const raw = row[key];
        const display = boolKeys.has(key) ? (raw ? "yes" : "no") : (raw?.toString().toLowerCase() ?? "");
        return display.includes(term.toLowerCase());
      })
    );
    setFilteredData(result);
    setCurrentPage(0);
  }, [searchInputs, data, config.columns]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.dir === "asc" ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const s = currentPage * rowsPerPage;
    return sortedData.slice(s, s + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage) || 1;

  const allSelected = sortedData.length > 0 && sortedData.every((r) => selectedRowIds[String(r.id)]);

  const toggleSelectAll = useCallback(() => {
    setSelectedRowIds(allSelected ? {} : Object.fromEntries(sortedData.map((r) => [String(r.id), true])));
  }, [allSelected, sortedData]);

  // Stable callbacks
  const handleSearch = useCallback((key: string, val: string) => {
    setSearchInputs((p) => ({ ...p, [key]: val }));
  }, []);

  const requestSort = useCallback((key: string) => {
    setSortConfig((p) => ({ key, dir: p.key === key && p.dir === "asc" ? "desc" : "asc" }));
  }, []);

  // Columns — only data cells, NO header inputs here
  const columns = useMemo(() => {
    const actionCols: any[] = [
      {
        id: "select",
        header: () => <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />,
        cell: (props: any) => (
          <input
            type="checkbox"
            checked={selectedRowIds[String(props.row.original.id)] ?? false}
            onChange={() =>
              setSelectedRowIds((prev) => {
                const n = { ...prev };
                const id = String(props.row.original.id);
                n[id] ? delete n[id] : (n[id] = true);
                return n;
              })
            }
          />
        ),
      },
    ];

    if (config.canEdit) {
      actionCols.push({
        id: "edit",
        header: () => null,
        cell: (props: any) => (
          <PencilIcon
            className="h-4 w-4 text-blue-500 cursor-pointer mx-auto"
            onClick={() => setModal({ open: true, row: props.row.original, isNew: false })}
          />
        ),
      });
    }

    if (config.canDelete) {
      actionCols.push({
        id: "delete",
        header: () => null,
        cell: (props: any) => (
          <TrashIcon
            className="h-4 w-4 text-red-500 cursor-pointer mx-auto"
            onClick={async () => {
              if (!window.confirm("Delete this record?")) return;
              const ok = await onDelete(String(props.row.original.id));
              if (ok?.status) {
                // onDataUpdate(data.filter((r) => String(r.id) !== String(props.row.original.id)));
                toast.success(ok?.data, { theme: "colored" });
              } else {
                toast.error(ok?.data, { theme: "colored" });
              }
            }}
          />
        ),
      });
    }

    const dataCols = visibleCols.map((col) =>
      columnHelper.accessor(col.key, {
        header: () => null, // header handled by SearchRow above
        cell: (props: any) => {
          const val = props.getValue();
          if (col.boolean) return <div className="text-center">{val ? "Yes" : "No"}</div>;
          return <div>{val ?? "—"}</div>;
        },
      })
    );

    return [...actionCols, ...dataCols];
  }, [
    visibleCols, selectedRowIds, allSelected, toggleSelectAll,
    config.canEdit, config.canDelete, data, onDataUpdate, onDelete,
  ]);

  const table = useReactTable({
    columns: columns as any,
    data: paginatedData,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
  });

  const bulkDelete = async () => {
    const ids = Object.keys(selectedRowIds);
    if (!ids.length) return toast.error("No rows selected", { theme: "colored" });
    if (!window.confirm(`Delete ${ids.length} record(s)?`)) return;
    setBulkDeleteMsg(true)
    const results = await  await onBulkDelete(ids);//Promise.all(ids.map((id) => onDelete(id)));
    if (results.every(Boolean)) {
      const strIds = new Set(ids);
      onDataUpdate(data.filter((r) => !strIds.has(String(r.id))));
      setSelectedRowIds({});
      toast.success("All selected rows are deleted successfully", { theme: "colored" });
    } else {
      toast.error("Some deletions failed, Please check.", { theme: "colored" });
    }
    setBulkDeleteMsg(false);
  };

  const exportExcel = () => {
    const ids = new Set(Object.keys(selectedRowIds));
    const rows = ids.size ? sortedData.filter((r) => ids.has(String(r.id))) : sortedData;
    if (!rows.length) return toast.error("No data to export", { theme: "colored" });
    const formatted = rows.map((row) => {
      const obj: Record<string, any> = {};
      visibleCols.forEach((col) => {
        const val = row[col.key];
        obj[col.label ?? col.key] = col.boolean ? (val ? "Yes" : "No") : (val ?? "");
      });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, config.title);
    XLSX.writeFile(wb, `${config.title}.xlsx`);
  };

  const handleModalSubmit = async (formData: Record<string, any>) => {
    const data = await onSave({ ...modal.row, ...formData }, modal.isNew);
    if (data?.status) {
      toast.success(data?.data, { theme: "colored" });
      setModal({ open: false, row: null, isNew: false });
    } else {
      toast.error("Save failed", { theme: "colored" });
    }
  };

  const modalType = modal.isNew ? config.newModal : config.editModal;

  return (
    <div className="w-full">
      {bulkDeleteMsg && <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-2 rounded-md shadow-sm mb-2">
        <p className="font-semibold text-sm">Processing in Progress</p>
        <p className="text-xs">
          The bulk delete operation is currently being processed on the backend and may take some time to complete.
        </p>
      </div>}
      {/* toolbar */}
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <label className="text-sm">
          Rows:
          <select
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(0); }}
            className="ml-2 border border-gray-300 rounded text-sm"
          >
            {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            <option value={sortedData.length}>All</option>
          </select>
        </label>
        <div className="flex gap-2">
          {config.canCreate && (
            <button
              onClick={() => setModal({ open: true, row: null, isNew: true })}
              className="flex items-center gap-1 bg-green-500 text-white text-sm py-1 px-3 rounded hover:bg-green-600"
            >
              <PlusIcon className="h-4 w-4" /> New
            </button>
          )}
          <button onClick={exportExcel} className="text-white text-sm py-1 px-3 rounded bg-[#ff7a29] hover:bg-[#ff6415]">
            Export to Excel
          </button>
          {config.canDelete && (
            <button
              onClick={bulkDelete}
              className={`bg-red-500 text-white text-sm py-1 px-3 rounded hover:bg-red-600 transition-opacity ${Object.keys(selectedRowIds).length === 0 ? "opacity-25 cursor-not-allowed" : "opacity-100"
                }`}
            >
              Delete Selected
            </button>
          )}
        </div>
      </div>

      {/* table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead>
            {/* SearchRow is outside TanStack — never re-mounts on data/selection change */}
            <SearchRow
              cols={visibleCols}
              sortConfig={sortConfig}
              onSearch={handleSearch}
              onSort={requestSort}
              allSelected={allSelected}       // ← add
              onSelectAll={toggleSelectAll}   // ← add
            />
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="border text-center py-8 text-gray-400">
                  No records found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`transition-colors ${selectedRowIds[String(row.original.id)]
                    ? "bg-[#e6f4ff] border-l-2 border-orange-400"
                    : "bg-white hover:bg-gray-50"
                    }`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="border p-2 text-center">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      <div className="flex justify-between items-center mt-3 text-sm text-gray-600">
        <span>Showing {paginatedData.length} of {filteredData.length} entries</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 0))}
            disabled={currentPage === 0}
            className="border border-gray-300 px-3 py-1 rounded disabled:opacity-40"
          >Prev</button>
          <span>Page {currentPage + 1} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages - 1))}
            disabled={currentPage >= totalPages - 1}
            className="border border-gray-300 px-3 py-1 rounded disabled:opacity-40"
          >Next</button>
        </div>
      </div>

      {/* modals */}
      {modal.open && (
        <>
          {modalType === "generic" && (
            <AddEditGroupModal
              isOpen={modal.open}
              onClose={() => setModal({ open: false, row: null, isNew: false })}
              onSubmit={handleModalSubmit}
              fields={config.genericModalFields ?? []}
              initialData={modal.row ?? {}}
              title={modal.isNew ? `Add New - ${config.title}` : `Edit - ${config.title}`}
            />
          )}
          {/* {modalType === "userEdit" && (
            <EditUserModal
              isOpen={modal.open}
              onClose={() => setModal({ open: false, row: null, isNew: false })}
              onEdit={() => { }}
              userId={modal.row?.id}
            />
          )}
          {modalType === "userNew" && (
            <NewUserModal
              isOpen={modal.open}
              onClose={() => setModal({ open: false, row: null, isNew: false })}
              onSuccess={() => { }}
            />
          )} */}
        </>
      )}
    </div>
  );
};

export default ReportTable;
